<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="$t('message.wizard.new.file')"
    @dismiss="onDismiss">

    <template slot="toolbar-items">
      <v-btn
        :loading="loading"
        @click="create"
        color="accent"
        dark flat
        :disabled="!validForm || loading"
      >{{ $t('message.wizard.createBtn') }}
      </v-btn>
    </template>

    <v-container py-3 fluid class="nx-taxonomy-wizard-body">
      <v-layout wrap fill-height>
        <v-flex class="md6 offset-md3 xs12">

          <nx-form class="nx-form" @form="flags = $event" ref="form">
            <div class="nx-form-fieldset">

              <nx-form-text
                :label="$t('message.form.fields.title')"
                name="name"
                v-model="draft.name"
                validation="required|min:3"
              ></nx-form-text>

              <nx-file-uploader
                :label="$t('message.wizard.file')"
                :hint="manualHintText"
                :selected-file="selectedFile"
                :before-upload-force-clip="beforeUploadForceClip"
                :before-upload-automatic-clip="beforeUploadAutomaticClip"
                :before-upload-clippings-config="beforeUploadClippingsConfig"
                :extensions="mimeType"
                @file="onFileSelected"
                @dismiss="clearFiles"
                ref="upload"
              ></nx-file-uploader>

            </div>
          </nx-form>
        </v-flex>
      </v-layout>
    </v-container>

  </nx-fullscreen-dialog>
</template>

<script>
import NxForm from '@/modules/form/Form';
import NxFormText from '@/modules/form/components/FormText';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import toast from '@/modules/toast/services';
import loadable from '@/mixins/loadable';
import { mapState, mapGetters } from 'vuex';
import { getImageDescription } from '@/utils/exif';
import NxFileUploader from '@/modules/upload/FileUploader';

export default {
  name: 'nx-file-wizard',
  mixins: [loadable],
  components: {
    NxFileUploader,
    NxForm,
    NxFormText,
    NxFullscreenDialog,
  },
  props: {
    active: {
      type: Boolean,
    },
    mimeType: {
      type: String,
    },
    type: {
      type: String,
      default: 'binaryFile',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    size: {
      type: Number,
    },
    beforeUploadForceClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadAutomaticClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadClippingsConfig: {
      type: Array,
      default: () => ([]),
    },
    manualHintText: {
      type: String,
    },
  },
  data() {
    return {
      draft: {
        name: null,
        type: this.type,
        collectionId: this.collectionId,
      },
      selectedFile: null,
      flags: {},
    };
  },
  computed: {
    ...mapState(['siteId']),
    ...mapGetters(['context']),
    validUpload() {
      return this.$refs.upload.valid && this.$refs.upload.files.length;
    },
    validForm() {
      return this.flags.valid && this.selectedFile;
    },
  },
  methods: {
    updateDraft(field, value) {
      this.draft = { ...this.draft, ...{ [field]: value } };
    },
    async create() {
      this.loading = true;
      // Crea el fichero en el back
      const fileUpload = await this.$store.dispatch(
        'upload/UPLOAD_WITH_PRESIGNED_URL', {
          file: this.selectedFile,
          type: this.type,
          name: this.draft.name,
          collectionId: this.collectionId,
        },
      );

      if (!fileUpload) {
        this.loading = false;
        return;
      }

      await fileUpload.uploadAllParts();
      const file = await fileUpload.completeUpload();
      this.$store.commit('upload/COMPLETE_FILE_UPLOAD', fileUpload);
      this.onSuccess(file);
    },
    clearFiles() {
      this.selectedFile = null;
    },
    onSuccess(file) {
      this.$emit('fileCreated', file);
      toast.notify(this.$t('message.wizard.createSuccess'));
      this.onDismiss();
    },
    onDismiss() {
      this.draft.name = undefined;
      this.$refs.upload.clear();
      this.$refs.form.resetFields();
      this.selectedFile = null;
      this.loading = false;
      this.$emit('dismiss');
    },
    onLoad(val) {
      if (val) {
        this.loadingStart();
      } else {
        this.loadingFinish();
      }
    },
    async onFileSelected(file) {
      let description = file.name;
      if (file.type.includes('image')) {
        description = await getImageDescription(file);
      }
      this.selectedFile = file;
      if (description && !this.draft.name) {
        this.updateDraft('name', description);
        await this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      }
    },
  },
};
</script>
